/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

import { throttle, isBoolean } from "underscore";
import baustein from "baustein";
import browser from "utils/browser";
import traversal from "dom/traversal";

const name = "app-header-callout";

const selectors = {
    LABEL: `.${name}__label`,
    CONTENT_TRIANGLE: `.${name}__content-triangle`,
    CONTENT: `.${name}__content`
};

const states = {
    ACTIVE: `${name}--active`,
    HOVERABLE: `${name}--hoverable`,
    CONTENT_ACTIVE: `${name}__content--active`,
    CONTENT_CENTRED: `${name}__content--centred`,
    CONTENT_RIGHT_ALIGNED: `${name}__content--right-aligned`,
    CONTENT_TRIANGLE_ACTIVE: `${name}__content-triangle--active`
};

export const events = {
    OPEN_LYST_MATTE: "appHeaderCalloutsNeedMatte",
    CLOSE_LYST_MATTE: "appHeaderCalloutsRemoveMatte",
    OPENED: "appHeaderCalloutOpened",
    CLOSED: "appHeaderCalloutClosed",
    TOGGLE: "appHeaderCalloutToggle",
    OPEN_LYST_MATTE_WITH_SCROLL: "OPEN_LYST_MATTE_WITH_SCROLL"
};

export default baustein.register(`${name}`, {
    /**
     * Whether the current callout is open or not.
     * @type {boolean}
     */
    open: false,

    defaultOptions: {
        /**
         * Whether the callout instance should align to the left, right or middle.
         * Supported alignment directions: right | centre.
         * If not alignment direction is supplied, it'll align to the left.
         * @type {string}
         */
        alignment: "",
        /**
         * Whether the callout instance should open via click events or not.
         * By default the callout instance will be displayed through hover events (via CSS).
         * @type {boolean}
         */
        byTap: false
    },

    init() {
        if (browser.hasTouch) {
            this.options.byTap = true;
        }
    },

    setupEvents(add) {
        this.setGlobalHandler("click", this._handleGlobalClick());

        add(events.TOGGLE, this._toggle);

        if (this.options.byTap && !this.options.disabled) {
            add("click", this._toggle);
        }
    },

    onInsert() {
        const contentEl = this.el.querySelector(selectors.CONTENT);

        this._positionCallout();

        if (!this.options.byTap && !this.options.disabled) {
            this.el.classList.add(states.HOVERABLE);
        }

        if (this.options.alignment === "centre") {
            return contentEl.classList.add(states.CONTENT_CENTRED);
        }

        if (this.options.alignment === "right") {
            contentEl.classList.add(states.CONTENT_RIGHT_ALIGNED);
        }
    },

    onRemove() {
        this.releaseGlobalHandler("click", this._handleGlobalClick);
    },

    /**
     * Displays or Hides the callout instance. Emits an event so that parent components can know
     * how many callouts instances are currently open.
     *
     * @see components/app-header _handleOpenCallouts()
     * @param {Object} event Baustein's wrapper around native DOM event object.
     * @param {boolean} force Whether to explicitly open or close the callout.
     * @returns {undefined}
     */
    _toggle({ type }, force) {
        const contentEl = this.el.querySelector(selectors.CONTENT);
        const triangleEl = this.el.querySelector(selectors.CONTENT_TRIANGLE);
        const currentState = contentEl.classList.contains(states.CONTENT_ACTIVE);
        const state = isBoolean(force) ? force : !currentState;

        let isOpen;

        if (this.options.disabled) {
            return;
        }

        contentEl.classList.toggle(states.CONTENT_ACTIVE, state);
        triangleEl.classList.toggle(states.CONTENT_TRIANGLE_ACTIVE, state);

        isOpen = contentEl.classList.contains(states.CONTENT_ACTIVE);

        if (isOpen) {
            this._positionCallout();
        }

        this.emit(isOpen ? events.OPENED : events.CLOSED);
        this.open = isOpen;

        if (type === events.TOGGLE) {
            // Prevent conflicting behavior with the component HOVERABLE state.
            setTimeout(() => {
                this.el.classList.toggle(states.HOVERABLE, !this.open);
            }, 300);
        }
    },

    /**
     * Positions the callout instance relative to an offset calculation.
     *
     * @return {undefined}
     */
    _positionCallout() {
        if (!this.el) {
            return;
        }
        const calloutOffset = this.el.getBoundingClientRect().height;
        const contentEl = this.el.querySelector(selectors.CONTENT);

        contentEl.style.top = `${calloutOffset}px`;
    },

    /**
     * Tries to hide the callout instance if any click doesn't fire within it.
     * Excludes click events within the callout instance content or on other callout instances.
     *
     * @return {Function}
     */
    _handleGlobalClick() {
        return throttle(event => {
            const containerEl = traversal.closest(event.target, `.${name}`);

            if (!this.open || (containerEl && containerEl === this.el)) {
                return;
            }

            this._toggle(event, false);
        }, 100);
    }
});

